import { useCallback, type MouseEvent } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Page, type PageScreenBackground } from "@boundary/layouts/pageLayout";
import styles from "./visualDomainPreview.module.scss";

const VISUAL_DOMAINS = ["print", "hub", "link", "referral", "sub"] as const;
export type VisualDomainId = (typeof VISUAL_DOMAINS)[number];

function isVisualDomain(id: string | undefined): id is VisualDomainId {
  return id !== undefined && VISUAL_DOMAINS.includes(id as VisualDomainId);
}

const SCREEN_BY_DOMAIN: Record<VisualDomainId, PageScreenBackground> = {
  print: "printDomain",
  hub: "hub",
  link: "linkDomain",
  referral: "referralDomain",
  sub: "sub"
};

const TITLES: Record<VisualDomainId, string> = {
  print: "Print — фон",
  hub: "Hub — фон",
  link: "Link — фон",
  referral: "Referral — фон",
  sub: "Sub — фон"
};

const HINTS: Record<VisualDomainId, string> = {
  print: "#2e2b46 → #5d5084 (плотнее hub, низ глубже #6d5f94).",
  hub: "#3c3962 → #6d5f94 (эталон мега-оболочки).",
  link: "#4a4574 → #6d5f94 (светлее hub, первый контакт).",
  referral: "#201d31 → #3c3962 (доп. сервис, чуть более «холодный»).",
  sub: "#222222 → #6d5f94 (вторичный хром)."
};

/** Превью фона домена для подбора визуала (dev); боевые экраны без изменений фона здесь */
export function VisualDomainPreviewPage() {
  const navigate = useNavigate();
  const { domain } = useParams<{ domain: string }>();

  const handleBack = useCallback(
    (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      navigate(-1);
    },
    [navigate]
  );

  if (!isVisualDomain(domain)) {
    return <Navigate to="/visual/print" replace />;
  }

  const screenBackground = SCREEN_BY_DOMAIN[domain];

  return (
    <Page screenBackground={screenBackground}>
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header
          back
          onBackClick={handleBack}
          center={<h1 className={styles.headerTitle}>{TITLES[domain]}</h1>}
        />
        <Page.DisplayZone.Body className={styles.content}>
          <p className={styles.lead}>{HINTS[domain]}</p>
          <nav className={styles.domainNav} aria-label="Другие домены">
            {VISUAL_DOMAINS.map((id) => (
              <button
                key={id}
                type="button"
                className={`${styles.domainNavButton}${id === domain ? ` ${styles.domainNavButtonActive}` : ""}`}
                onClick={() => navigate(`/visual/${id}`)}
              >
                {id}
              </button>
            ))}
          </nav>
          <div className={styles.servicesGrid}>
            <div className={styles.stubCard}>Блок A</div>
            <div className={styles.stubCard}>Блок B</div>
            <div className={styles.stubCard}>Блок C</div>
            <div className={styles.stubCard}>Блок D</div>
          </div>
        </Page.DisplayZone.Body>
      </Page.DisplayZone>
    </Page>
  );
}
